import React, { useState } from 'react';
import { motion } from 'framer-motion';
import StatusBadge from '../../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/StatusBadge/StatusBadge';
import RatingStars from '../RatingStars/RatingStars';
import CustomCheckbox from '../../../../../components/CustomCheckbox/CustomCheckbox';
import ProspectedBadge from '../../../../Dashboard/components/ResultsSection/ResultsTable/ResultsTableRow/ProspectedBadge/ProspectedBadge';
import ConfirmationModal from '../../../../../components/ConfirmationModal/ConfirmationModal';
import LeadDetailModal from '../../LeadDetailModal/LeadDetailModal';
import EditLeadModal from '../../EditLeadModal/EditLeadModal';
import { SavedLead } from '../../../../../services/leadsService';
import { useTranslation } from 'react-i18next';
import { getWhatsAppLink } from '../../../../../utils/whatsappUtils';

interface Lead extends SavedLead { }

interface LeadsTableRowProps {
  lead: Lead;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: (id: string) => void;
  onLeadUpdated: (updatedLead: SavedLead) => void;
  index: number;
}

const LeadsTableRow: React.FC<LeadsTableRowProps> = ({
  lead,
  isSelected,
  onSelect,
  onDelete,
  onLeadUpdated,
  index
}) => {
  const { t } = useTranslation();
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleEdit = () => setIsEditModalOpen(true);

  const handleConfirmDelete = () => {
    onDelete(lead.id);
    setIsDeleteModalOpen(false);
  };

  const handleRowClick = (e: React.MouseEvent) => {
    // Previne que o clique na linha interfira se o clique for em um botão de ação
    const target = e.target as HTMLElement;
    if (target.closest('button') || target.closest('a') || target.closest('input[type="checkbox"]')) {
      return;
    }
    onSelect();
  };

  const rowClasses = isSelected
    ? 'bg-primary/10 dark:bg-primary/20 hover:bg-primary/20 dark:hover:bg-primary/30'
    : 'hover:bg-gray-50 dark:hover:bg-background-dark-elevated2';

  return (
    <>
      <motion.tr
        className={`transition-colors cursor-pointer ${rowClasses}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.05, duration: 0.3 }}
        whileHover={{ scale: 1.01 }}
        onClick={handleRowClick}
      >
        <td className="p-4">
          <CustomCheckbox
            checked={isSelected}
            onChange={onSelect}
          />
        </td>
        <td className="p-4">
          <p className="font-semibold text-[#181411] dark:text-white">{lead.company_name}</p>
          {lead.website && (
            <a className="text-sm text-primary hover:underline" href={`http://${lead.website}`} target="_blank" rel="noopener noreferrer">
              {lead.website}
            </a>
          )}
        </td>
        <td className="p-4 text-gray-600 dark:text-gray-200">{lead.phone_number}</td>
        <td className="p-4 text-gray-600 dark:text-gray-200 text-sm">{lead.location || '-'}</td>
        <td className="p-4 text-center">
          <StatusBadge status={lead.whatsapp_status} whatsapp={lead.whatsapp_status === 'Validado'} />
        </td>
        <td className="p-4 text-center">
          <ProspectedBadge prospected={lead.prospected} />
        </td>
        <td className="p-4 text-center">
          <RatingStars rating={lead.rating} />
        </td>
        <td className="p-4">
          <div className="flex items-center justify-start gap-2">
            {lead.phone_number && (
              <motion.a
                href={getWhatsAppLink(lead.phone_number) || '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title={t('Enviar mensagem no WhatsApp')}
                onClick={(e) => e.stopPropagation()}
              >
                <img
                  src={`${process.env.PUBLIC_URL}/whatsapp-logo.png`}
                  alt="WhatsApp"
                  className="w-5 h-5"
                />
              </motion.a>
            )}
            <motion.button
              onClick={handleEdit}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={t('Editar')}
            >
              <span className="material-symbols-outlined text-lg text-gray-600 dark:text-gray-400">edit</span>
            </motion.button>
            <motion.button
              onClick={() => setIsViewModalOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={t('Visualizar')}
            >
              <span className="material-symbols-outlined text-lg text-gray-600 dark:text-gray-400">visibility</span>
            </motion.button>
            <motion.button
              onClick={() => setIsDeleteModalOpen(true)}
              className="p-2 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              title={t('dashboard.action_delete')}
            >
              <span className="material-symbols-outlined text-lg text-red-600 dark:text-red-400">delete</span>
            </motion.button>
          </div>
        </td>
      </motion.tr>

      <LeadDetailModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        lead={lead}
      />

      <EditLeadModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        lead={lead}
        onLeadUpdated={onLeadUpdated}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('modals.confirm_delete_title')}
        message={t('Você tem certeza que deseja excluir este lead? Esta ação não pode ser desfeita.')}
        confirmText={t('Sim, Excluir')}
        cancelText={t('modals.cancel')}
      />
    </>
  );
};

export default LeadsTableRow;